import { Button, Checkbox, Col, Flex, Form, Input, Row, Select, Spin, message, notification } from 'antd';
import Label from 'components/Common/Label/Label';
import TemplateListDropdown from 'components/Common/templateListDropdown/TemplateListDropdown';
import TextGenerator from 'components/Common/TextGenerator/TextGenerator';
import React, { useEffect, useState } from 'react';
import { isValidEmail } from 'utils/utils';
import { addExternalContributors, fetchAllContributors, fetchEmailTemplate } from "api/project.api";
import { notificationController } from "components/Common/Notification/Notification";
import { ShareAltOutlined } from '@ant-design/icons';
import { ScoreCardSection, ScoreCardState } from 'utils/scoreCard.type';

type Contributor = {
    id: number;
    name: string;
    email: string;
};

interface LimitedAccessFormProps {
    hideCheckboxes: boolean;
    selectedPool: any;
    report_id: any;
    onCancel: () => void;
}

const LimitedAccessForm = ({ hideCheckboxes, selectedPool, report_id, onCancel }: LimitedAccessFormProps) => {
    const [contributors, setContributors] = useState<Contributor[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [loadingTemplate, setLoadingTemplate] = useState(false);
    const [showContactInfo, setShowContactInfo] = useState<boolean>(true);
    const [selectedEmail, setSelectedEmail] = useState<string[]>([]);
    const [templateData, setTemplateData] = useState<any>(null);
    const [emailTemplateId, setEmailTemplateId] = useState<any>(null);
    const [ccEmail, setCCEmail] = useState<any>(null);
    const [emailSubject, setEmailSubject] = useState<any>("");
    const [emailBody, setEmailBody] = useState<any>(null);
    const [messageText, setMessageText] = useState("");
    const [instruction, setInstruction] = useState<string[]>([]);
    const [form] = Form.useForm();

    const handleCopyDomain = () => {
        const domain = window.location.origin;
        navigator.clipboard
            .writeText(domain)
            .then(() => {
                message.success("Link copied to clipboard!");
            })
            .catch(() => {
                message.error("Failed to copy link.");
            });
    };
    const [scoreCardInfo, setScoreCardInfo] = useState<ScoreCardState>({
        overview: false,
        profileAnalysis: false,
        interviewAnalysis: false,
        prescreenAnalysis: false,
        cvAnalysisScore: false
    });

    const scoreCardSections: ScoreCardSection[] = [
        { key: 'overview', label: 'Overview' },
        { key: 'profileAnalysis', label: 'Profile Analysis' },
        { key: 'interviewAnalysis', label: 'Interview Analysis' },
        { key: 'prescreenAnalysis', label: 'Prescreen Analysis' },
        { key: 'cvAnalysisScore', label: 'CV Analysis Score' },
    ];

    const fetchAllContributor = async () => {
        try {
            setLoading(true);
            const response: any = await fetchAllContributors(selectedPool);
            setContributors(response);
        } catch (error) {
            console.error("Error fetching contributor list:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleFetchEmailTemplate = async () => {
        try {
            setLoadingTemplate(true);
            const response: any = await fetchEmailTemplate(selectedPool, emailTemplateId);
            setEmailSubject(response?.email_subject || "");
            setEmailBody(response?.email_body || "");
            form.setFieldsValue({
                subject: response?.email_subject || "",
                content: response?.email_body || "",
            });
            setMessageText(response?.email_body || "");
        } catch (error) {
            notification.error({
                message: "Error",
                description: "Failed to load email template",
            });
        } finally {
            setLoadingTemplate(false);
        }
    };

    const handleAddContributors = async (values: any) => {
        try {
            const { emails, cc_emails, subject, content } = values;

            if (emails.length === 0) {
                notificationController.error({
                    message: "Error",
                    description: "Please add at least one recipient email",
                });
                return;
            }

            setIsSubmitting(true);
            const scoreCardInfoToShare = hideCheckboxes ? undefined : scoreCardInfo;
            const response: any = await addExternalContributors(
                emails,
                selectedPool,
                showContactInfo,
                cc_emails,
                content,
                subject,
                scoreCardInfoToShare,
                report_id
            );

            notification.success({
                message: "Success",
                description: (
                    <div style={{ whiteSpace: "pre-wrap" }}>
                        {Array.isArray(response?.message)
                            ? response.message.join(",\n")
                            : response?.message ?? "Successfully added external contributors"}
                    </div>
                ),
            });
            handleCancel();
        } catch (error) {
            console.error("Validation failed:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    useEffect(() => {
        if (emailTemplateId) {
            handleFetchEmailTemplate();
        }
    }, [emailTemplateId]);

    useEffect(() => {
        fetchAllContributor();
    }, [selectedPool]);

    return (
        <Flex vertical gap={8}>
            <Form
                id="limited-access-form"
                form={form}
                layout="vertical"
                initialValues={{ subject: "" }}
                onFinish={handleAddContributors}
            >
                <Spin spinning={loadingTemplate || loading || isSubmitting}>

                    {!hideCheckboxes &&
                        <Flex vertical gap={12} className="my-4">
                            <span className="font-semibold">Info To Share</span>
                            <Row gutter={[16, 12]}>
                                <Col span={8}>
                                    <Checkbox
                                        checked={!showContactInfo}
                                        onChange={() => setShowContactInfo(!showContactInfo)}
                                        className="text-sm text-text-secondary font-medium cursor-pointer w-full"
                                    >
                                        <span className="-ml-1">Hide Personal Details</span>
                                    </Checkbox>
                                </Col>
                                {scoreCardSections.map((section) => (
                                    <Col span={8} key={section.key}>
                                        <Checkbox
                                            checked={scoreCardInfo[section.key]}
                                            onChange={(e) => setScoreCardInfo((prev: any) => ({
                                                ...prev,
                                                [section.key]: e.target.checked
                                            }))}
                                            className="text-sm text-text-secondary font-medium cursor-pointer w-full"
                                        >
                                            {section.label}
                                        </Checkbox>
                                    </Col>
                                ))}
                            </Row>
                        </Flex>}

                    <Flex vertical gap={8} className="mt-5">
                        <Flex vertical gap={2}>
                            <Label
                                labelTitle="Email(s)"
                                isHelpIconRequired={true}
                                isRequired={true}
                                tooltipText="Recipients Email(s)"
                            />
                            <Flex gap={10} className="mt-1">
                                <Form.Item
                                    name="emails"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Please add at least one recipient email!'
                                        },
                                        {
                                            validator: (_, value) => {
                                                if (value && value.some((email: string) => !isValidEmail(email))) {
                                                    return Promise.reject(new Error('Please enter valid email addresses!'));
                                                }
                                                return Promise.resolve();
                                            }
                                        }
                                    ]}
                                    style={{ marginBottom: 0, width: "100%" }}
                                >
                                    <Select
                                        mode="tags"
                                        allowClear
                                        style={{ width: '100%' }}
                                        placeholder="Enter or select email addresses"
                                        onChange={(values) => {
                                            const uniqueEmails = Array.from(new Set(values.map((email: any) => email.trim()))) as any;
                                            setSelectedEmail(uniqueEmails);
                                        }}
                                        size="large"
                                    />
                                </Form.Item>
                            </Flex>
                        </Flex>
                    </Flex>

                    <Flex vertical gap={8} className="mt-5">
                        <span className="text-sm text-text-secondary font-medium">Email Template</span>
                        <Flex gap={10}>
                            <TemplateListDropdown
                                type="EXTERNAL_CONTRIBUTOR_EMAIL_TEMPLATE"
                                size="large"
                                style={{ width: "100%", background: "#fff" }}
                                placeholder="Please select template"
                                onChange={(templateId: any, templateDetails: any) => {
                                    setTemplateData({
                                        ...templateDetails.details,
                                        emailSignature: templateDetails.details.signature,
                                    });
                                    setEmailTemplateId(templateDetails.details.id);
                                }}
                            />
                        </Flex>
                    </Flex>

                    <Flex vertical gap={8} className="mt-5">
                        <span className="text-sm text-text-secondary font-medium">Cc</span>
                        <Flex gap={10}>
                            <Form.Item
                                name="cc_emails"
                                rules={[
                                    {
                                        validator: (_, value) => {
                                            if (value && value.some((email: string) => !isValidEmail(email))) {
                                                return Promise.reject(new Error('Please enter valid email addresses!'));
                                            }
                                            return Promise.resolve();
                                        }
                                    }
                                ]}
                                style={{ marginBottom: 0, width: "100%" }}
                            >
                                <Select
                                    mode="tags"
                                    allowClear
                                    style={{ width: '100%' }}
                                    placeholder="Enter or select CC email addresses"
                                    onChange={(values) => {
                                        const uniqueEmails = Array.from(new Set(values.map((email: any) => email.trim())));
                                        setCCEmail(uniqueEmails);
                                    }}
                                    size="large"
                                />
                            </Form.Item>
                        </Flex>
                    </Flex>

                    <Flex vertical gap={8} className="mt-5">
                        <span className="text-sm text-text-secondary font-medium">Subject</span>
                        <Flex gap={10}>
                            <Form.Item
                                name="subject"
                                rules={[
                                    { required: true, message: 'Please enter an email subject!' },
                                    { max: 100, message: 'Subject cannot exceed 100 characters!' }
                                ]}
                                style={{ marginBottom: 0, width: "100%" }}
                            >
                                <Input
                                    size="large"
                                    placeholder="Enter Email Subject"
                                    onChange={(e) => setEmailSubject(e.target.value)}
                                />
                            </Form.Item>
                        </Flex>
                    </Flex>

                    <Row gutter={24} className="mt-5">
                        <Col span={6}>
                            <div style={{ marginBottom: 8 }}>
                                <span className="text-sm font-medium text-text-secondary">
                                    Message <span className="text-primary">*</span>
                                </span>
                                <div className="text-xs text-gray-400">Write a short message</div>
                            </div>
                        </Col>

                        <Col span={18}>
                            <TextGenerator
                                onComplete={() => setInstruction([])}
                                onChange={(text) => form.setFieldValue("content", text)}
                                text={messageText}
                                newInstruction={instruction}
                            >
                                <Form.Item
                                    name="content"
                                    rules={[
                                        { required: true, message: 'Please enter an email message!' },
                                        { min: 10, message: 'Message should be at least 10 characters long!' }
                                    ]}
                                    style={{ marginBottom: 0, width: "100%" }}
                                >
                                    <Input.TextArea
                                        placeholder="Email Content"
                                        rows={10}
                                        onChange={(e) => {
                                            const newText = e.target.value;
                                            setMessageText(newText);
                                            setEmailBody(newText);
                                        }}
                                    />
                                </Form.Item>
                            </TextGenerator>
                        </Col>
                    </Row>
                </Spin>
                <div style={{ display: "flex", justifyContent: "end", gap: "12px" }} className="items-end my-4">
                    <a
                        type="default"
                        className="text-primary border-primary text-nowrap underline hover:underline mx-4"
                        onClick={handleCopyDomain}
                        style={{ height: 27 }}
                    >
                        Copy Link
                    </a>
                    <Button onClick={handleCancel} style={{ borderRadius: 4 }}>
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        htmlType="submit"
                        form="limited-access-form"
                        style={{ borderRadius: 4 }}
                    >
                        <ShareAltOutlined style={{ fontSize: '16px' }} /> Share
                    </Button>
                </div>
            </Form>
        </Flex>
    );
};

export default LimitedAccessForm;